(() => {
  const preferenceParam = 'preferred_style';
  const filterParam = 'filter.p.m.custom.main_product_style';
  const disabledParam = 'soft_preferences';
  const validStyle = (value) => /^gid:\/\/shopify\/Metaobject\/\d+$/.test(value || '');
  const validPreference = (value) => /^gid:\/\/shopify\/(?:Metaobject|TaxonomyValue)\/\d+$/.test(value || '');
  const normalizeStyle = (value) => /^\d+$/.test(value || '') ? `gid://shopify/Metaobject/${value}` : value;
  const productStyle = normalizeStyle(document.currentScript?.dataset.productStyle);

  if (validStyle(productStyle)) {
    const carryStyle = (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || event.defaultPrevented) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/collections\/[^/]+(?:\/[^/]+)?\/?$/.test(url.pathname)) return;
      url.searchParams.set(preferenceParam, productStyle);
      url.searchParams.delete('page');
      link.href = url.href;
    };
    document.addEventListener('click', carryStyle, { capture: true });
    document.addEventListener('auxclick', carryStyle, { capture: true });
  }

  class CollectionStylePreference extends HTMLElement {
    connectedCallback() {
      if (this.dataset.styleEnabled !== 'true' && this.dataset.softEnabled !== 'true') return;
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      customElements.whenDefined('facet-filters-form').then(() => {
        if (!this.isConnected) return;
        const params = new URLSearchParams(window.location.search);
        this.preparePagination(params);
        if (!this.canPrioritize(params)) return;
        if (params.has('page')) {
          params.delete('page');
          history.replaceState({ searchParams: params.toString() }, '', `${location.pathname}?${params}`);
        }
        this.update(params, null, false, true);
      });
    }

    disconnectedCallback() {
      this.run?.controller.abort();
      this.removeEventListener('click', this.onClick);
    }

    canPrioritize(params) {
      const preferences = this.getPreferences(params);
      return Boolean(preferences.style || preferences.defaults.length);
    }

    getPreferences(params) {
      if (params.get(disabledParam) === 'off') return { style: null, defaults: [] };
      const style = this.dataset.styleEnabled === 'true' && this.dataset.styleFilterAvailable === 'true' &&
        validStyle(params.get(preferenceParam)) && !params.getAll(filterParam).some(Boolean)
        ? { filter: filterParam, values: [params.get(preferenceParam)], field: 'productStyle' } : null;
      const defaults = [];
      if (this.dataset.softEnabled === 'true') {
        for (const [filter, selected, field] of [
          [this.dataset.softSeasonParam, this.dataset.softSeasons, 'productSeasons'],
          [this.dataset.softCategoryParam, this.dataset.softCategories, 'productCategory'],
        ]) {
          const values = [...new Set((selected || '').split(',').map(normalizeStyle).filter(validPreference))];
          if (filter && values.length && !params.getAll(filter).some(Boolean)) defaults.push({ filter, values, field });
        }
      }
      return { style, defaults };
    }

    matchesRules(card, rules) {
      return rules.every((rule) => {
        const values = (card.dataset[rule.field] || '').split(',').map(normalizeStyle);
        return rule.values.some((value) => values.includes(value));
      });
    }

    prepareSearchParams(searchParams, updateURLHash) {
      const params = new URLSearchParams(searchParams);
      const current = new URLSearchParams(window.location.search).get(preferenceParam);
      if (updateURLHash && validStyle(current) && !params.has(preferenceParam) && !this.clearing) {
        params.set(preferenceParam, current);
      }
      if (updateURLHash && new URLSearchParams(location.search).get(disabledParam) === 'off' &&
          !params.has(disabledParam) && !this.clearing) params.set(disabledParam, 'off');
      return params.toString();
    }

    renderStylePage(searchParams, event, updateURLHash) {
      const params = new URLSearchParams(searchParams);
      this.pageParams = params;
      this.run?.controller.abort();
      if (!this.canPrioritize(params)) {
        this.run = null;
        this.setBusy(false);
        return false;
      }
      params.delete('page');
      this.update(params, event, updateURLHash);
      return true;
    }

    preparePagination(params = this.pageParams || new URLSearchParams(location.search)) {
      if (params.get(disabledParam) !== 'off') return;
      this.querySelectorAll('.pagination-wrapper a[href]').forEach((link) => {
        const url = new URL(link.href, location.href);
        url.searchParams.set(disabledParam, 'off');
        link.href = url.href;
      });
    }

    async fetchPage(run, params, page = 1) {
      const query = new URLSearchParams(params);
      query.delete(preferenceParam);
      query.delete(disabledParam);
      query.set('section_id', this.dataset.sectionId);
      if (page > 1) query.set('page', page);
      else query.delete('page');
      const url = `${location.pathname}?${query}`;
      if (run.cache.has(url)) return run.cache.get(url);
      const response = await fetch(url, { signal: run.controller.signal });
      if (!response.ok) throw new Error(`Collection response ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = doc.getElementById('ProductGridContainer');
      if (!grid || Number(grid.dataset.page) !== page) throw new Error('Invalid collection response');
      const result = { doc, grid };
      run.cache.set(url, result);
      if (run.cache.size > 8) run.cache.delete(run.cache.keys().next().value);
      return result;
    }

    createStream(params, rules, source) {
      return {
        params, rules,
        queue: source ? [...source.grid.querySelectorAll('#product-grid > [data-style-product-id]')] : [],
        next: source ? Number(source.grid.dataset.nextPage) || 0 : 1,
      };
    }

    async nextBatch(run) {
      // Commit cursors only after the complete batch loads, so retries cannot skip cards.
      const streams = run.streams.map((stream) => ({ ...stream, queue: [...stream.queue] }));
      const seen = new Set(run.seen);
      const cards = [];
      for (const stream of streams) {
        while (cards.length < run.pageSize && seen.size < run.total) {
          if (!stream.queue.length) {
            if (!stream.next) break;
            const source = await this.fetchPage(run, stream.params, stream.next);
            stream.queue = [...source.grid.querySelectorAll('#product-grid > [data-style-product-id]')];
            stream.next = Number(source.grid.dataset.nextPage) || 0;
            if (stream.queue.some((card) => !this.matchesRules(card, stream.rules))) throw new Error('Preference filter was not applied');
          }
          const card = stream.queue.shift();
          if (!card) continue;
          const id = card.dataset.styleProductId;
          if (seen.has(id)) continue;
          seen.add(id);
          cards.push(card.cloneNode(true));
        }
      }
      return { cards, streams, seen };
    }

    async update(params, event, updateURLHash, initial = false) {
      this.run?.controller.abort();
      const run = {
        controller: new AbortController(), cache: new Map(), params,
        preferences: this.getPreferences(params), seen: new Set(), pageSize: Number(this.dataset.pageSize),
        updateEvent: initial ? null : FacetFiltersForm.startUpdateEvent(params.toString()),
      };
      this.run = run;
      this.retry = () => this.update(params, event, updateURLHash, initial);
      this.setBusy(true);
      const baseParams = new URLSearchParams(params);
      baseParams.delete(preferenceParam);
      baseParams.delete(disabledParam);
      try {
        const initialDoc = initial && Number(this.dataset.page) === 1
          ? new DOMParser().parseFromString(this.outerHTML, 'text/html') : null;
        const base = initialDoc ? { doc: initialDoc, grid: initialDoc.getElementById(this.id) }
          : await this.fetchPage(run, baseParams);
        if (this.run !== run) return;
        run.base = base;
        run.total = Number(base.grid.dataset.productCount);
        const { style, defaults } = run.preferences;
        const groups = style ? defaults.length ? [[style, ...defaults], [style], defaults, []] : [[style], []]
          : [defaults, []];
        run.streams = groups.map((rules) => {
          const query = new URLSearchParams(baseParams);
          rules.forEach((rule) => query.set(rule.filter, rule.values.join(',')));
          return this.createStream(query, rules, rules.length ? null : base);
        });
        const batch = await this.nextBatch(run);
        if (this.run !== run) return;
        const rendered = base.grid.cloneNode(true);
        const grid = rendered.querySelector('#product-grid');
        if (grid?.tagName === 'UL') grid.replaceChildren(...batch.cards);
        if (grid && !await this.prepareGridImages(grid, run.controller.signal)) {
          if (run.controller.signal.aborted) return;
          throw new Error('Collection images unavailable');
        }
        if (this.run !== run) return;
        rendered.querySelector('.pagination-wrapper')?.remove();
        base.grid.innerHTML = rendered.innerHTML;
        if (initial) {
          this.innerHTML = rendered.innerHTML;
        } else {
          const html = base.doc.documentElement.outerHTML;
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          if (document.getElementById('ProductCount')) FacetFiltersForm.renderProductCount(html, run.updateEvent);
          else run.updateEvent?.resolve(run.total);
        }
        Object.assign(run, batch);
        this.showControls(run);
        this.prepareCards();
        FacetFiltersForm.searchParamsPrev = params.toString();
        if (updateURLHash) FacetFiltersForm.updateURLHash(params.toString());
      } catch (error) {
        if (error.name === 'AbortError' || this.run !== run) return;
        run.updateEvent?.reject(error);
        // A failed preference must never hide the ordinary collection or its hard filters.
        if (run.base && !initial) {
          const html = run.base.doc.documentElement.outerHTML;
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          if (document.getElementById('ProductCount')) FacetFiltersForm.renderProductCount(html);
          if (updateURLHash) FacetFiltersForm.updateURLHash(params.toString());
        }
        this.showError();
      } finally {
        if (this.run === run) this.setBusy(false);
      }
    }

    async loadMore() {
      const run = this.run;
      if (!run || this.hasAttribute('aria-busy') && this.getAttribute('aria-busy') === 'true') return;
      this.retry = () => this.loadMore();
      this.querySelector('.collection-style-preference__error')?.setAttribute('hidden', '');
      this.setBusy(true);
      try {
        const batch = await this.nextBatch(run);
        const fragment = document.createDocumentFragment();
        fragment.append(...batch.cards);
        const grid = this.querySelector('#product-grid');
        if (!await this.prepareGridImages(fragment, run.controller.signal)) {
          if (run.controller.signal.aborted) return;
          throw new Error('Collection images unavailable');
        }
        if (this.run !== run) return;
        grid.append(fragment);
        Object.assign(run, batch);
        this.updateControls(run);
        this.prepareCards();
      } catch (error) {
        if (error.name !== 'AbortError' && this.run === run) this.showError();
      } finally {
        if (this.run === run) this.setBusy(false);
      }
    }

    showControls(run) {
      const template = this.querySelector('[data-style-controls]');
      if (!template) return;
      const controls = template.content.cloneNode(true);
      const clear = controls.querySelector('[data-style-clear]');
      if (run.preferences.defaults.length && clear?.dataset.defaultLabel) clear.textContent = clear.dataset.defaultLabel;
      this.querySelector('.collection').prepend(controls.querySelector('.collection-style-preference__toolbar'));
      this.append(controls);
      this.updateControls(run);
    }

    updateControls(run) {
      const more = this.querySelector('[data-style-more]');
      if (more) more.hidden = run.seen.size >= run.total;
      const status = this.querySelector('[data-style-status]');
      if (status) status.textContent = status.dataset.message
        .replace('[count]', run.seen.size).replace('[total]', run.total);
    }

    showError() {
      let error = this.querySelector('.collection-style-preference__error');
      if (!error) {
        error = this.querySelector('[data-style-controls]')?.content
          .querySelector('.collection-style-preference__error').cloneNode(true);
        if (error) this.append(error);
      }
      error?.removeAttribute('hidden');
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('[data-style-more], [data-style-retry]').forEach((button) => { button.disabled = busy; });
      document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
        .forEach((spinner) => spinner.classList.toggle('hidden', !busy));
      document.getElementById('ProductCount')?.classList.toggle('loading', busy);
      document.getElementById('ProductCountDesktop')?.classList.toggle('loading', busy);
    }

    prepareCards() {
      this.querySelectorAll('.scroll-trigger').forEach((card) => card.classList.add('scroll-trigger--cancel'));
    }

    prepareGridImages(root, signal) {
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      const columns = desktop
        ? Number(this.querySelector('#product-grid')?.className.match(/grid--(\d+)-col-desktop/)?.[1]) || 4 : 2;
      const readyCount = columns * (desktop ? 2 : 3);
      prepareProductCardImages(root, columns * (desktop ? 1 : 2), signal, readyCount);
      return prepareProductCardImages(root, readyCount, signal);
    }

    handleClick(event) {
      if (event.target.closest('[data-style-clear]')) {
        this.clearing = true;
        const params = new URLSearchParams(location.search);
        params.delete(preferenceParam);
        params.delete('page');
        if (this.dataset.softEnabled === 'true') params.set(disabledParam, 'off');
        FacetFiltersForm.renderPage(params.toString(), event);
        this.clearing = false;
      } else if (event.target.closest('[data-style-more]')) {
        this.loadMore();
      } else if (event.target.closest('[data-style-retry]')) {
        this.retry?.();
      }
    }
  }

  if (!customElements.get('collection-style-preference')) {
    customElements.define('collection-style-preference', CollectionStylePreference);
  }
})();
